import Pagination from "../pagination/Pagination"
import { getCaseTypeFromId } from "../../../../lib/labels"
import HeaderCell from "../../cells/HeaderCell"
import HeaderCellGroup from "../../cells/HeaderCellGroup"
import NameCell from "../../cells/NameCell"
import classes from '../WorkQComponent.module.css'
import TextCellCopy from "../../cells/TextCellCopy"
import DateWithBox from "../../cells/DateWithBox"
import DateDisplayWithNmuberOfDays from "../../../common/DateDisplayWithNmuberOfDays"
import FollowUpQStatus from "./followup/FollowUpQStatus"
import WorkQComponentLoading from "./WorkQComponentLoading"
import NoteCell from "../../cells/NoteCell"
import { dateTimeIsPastPeriod, dayOneStatusClass } from "../../../../lib/dateScripts"
import { miscellaneous } from "../../../../lib/misc"
import { retrieveSpecificWorkQCleaned } from "../../../../lib/systemData"
import { useEffect, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { socket } from "../../../../socket"
import { deletedRecordIds, removeDeletedIds, withoutDeletedQueueFields } from "../../../../lib/noteDelete"
import { applyDeletedNotesPatch } from "../../../../lib/applyNoteSoftDelete"

export default function FollowUpQ({workqSlug, patientsList}) {
    const dispatch = useDispatch()
    const [renderedRows, setRenderedRows] = useState([])
    const appointmentSubscriptionsRef = useRef([])
    const caseSubscriptionsRef = useRef([])

    const clearAppointmentSubscriptions = () => {
        appointmentSubscriptionsRef.current.forEach(({ eventName, handler }) => {
            socket.off(eventName, handler)
        })
        appointmentSubscriptionsRef.current = []
    }

    const clearCaseSubscriptions = () => {
        caseSubscriptionsRef.current.forEach(({ eventName, handler }) => {
            socket.off(eventName, handler)
        })
        caseSubscriptionsRef.current = []
    }

    useEffect(() => {
        setRenderedRows(patientsList?.data || [])
    }, [patientsList?.data])

    useEffect(() => {
        const rows = patientsList?.data || []
        clearAppointmentSubscriptions()
        if (!rows.length) {
            return
        }

        if (!socket.connected) {
            socket.connect()
        }

        const subscriptions = rows
            .map((row) => {
                const appointmentId = row?.appointment?.id
                if (!appointmentId) {
                    return null
                }

                const eventName = `appointment_followup_${appointmentId}`
                const handler = (payload) => {
                    const rawIncoming = payload?.data ?? payload?.row ?? payload
                    if (!rawIncoming || typeof rawIncoming !== 'object') {
                        return
                    }
                    applyDeletedNotesPatch(dispatch, [
                        ...(Array.isArray(rawIncoming.notes) ? rawIncoming.notes : []),
                        ...(Array.isArray(rawIncoming.task_notes) ? rawIncoming.task_notes : []),
                        ...(Array.isArray(rawIncoming.tasks) ? rawIncoming.tasks : []),
                    ], row?.case?.id)
                    const incomingData = withoutDeletedQueueFields(rawIncoming)

                    setRenderedRows((prevRows) =>
                        prevRows.map((prevRow) => {
                            if (prevRow?.appointment?.id !== appointmentId) {
                                return prevRow
                            }

                            return {
                                ...prevRow,
                                ...incomingData,
                                case: {
                                    ...prevRow?.case,
                                    ...incomingData?.case,
                                    id: prevRow?.case?.id,
                                },
                                appointment: {
                                    ...prevRow?.appointment,
                                    ...incomingData?.appointment,
                                    id: prevRow?.appointment?.id ?? incomingData?.appointment?.id,
                                },
                                workq_call_follow_up: {
                                    ...prevRow?.workq_call_follow_up,
                                    ...incomingData?.workq_call_follow_up,
                                },
                            }
                        }),
                    )
                }

                socket.on(eventName, handler)
                return { eventName, handler }
            })
            .filter(Boolean)

        appointmentSubscriptionsRef.current = subscriptions
        return clearAppointmentSubscriptions
    }, [patientsList?.data, dispatch])

    useEffect(() => {
        const rows = patientsList?.data || []
        clearCaseSubscriptions()
        if (!rows.length) {
            return
        }

        if (!socket.connected) {
            socket.connect()
        }

        const uniqueCaseIds = [...new Set(rows.map((row) => row?.case?.id).filter(Boolean))]

        const subscriptions = uniqueCaseIds.map((caseId) => {
            const eventName = `case-updated-${caseId}`
            const handler = (payload) => {
                const incomingData = payload?.data ?? payload?.row ?? payload
                const dropIds = new Set([
                    ...deletedRecordIds(incomingData?.notes),
                    ...deletedRecordIds(incomingData?.task_notes),
                    ...deletedRecordIds(incomingData?.tasks),
                ].map((id) => String(id)))
                applyDeletedNotesPatch(dispatch, [
                    ...(Array.isArray(incomingData?.notes) ? incomingData.notes : []),
                    ...(Array.isArray(incomingData?.task_notes) ? incomingData.task_notes : []),
                    ...(Array.isArray(incomingData?.tasks) ? incomingData.tasks : []),
                ], caseId)
                const incomingAicaDepartmentStatus = incomingData?.aica_department_status ?? incomingData?.case?.aica_department_status
                if (!incomingAicaDepartmentStatus && dropIds.size === 0) {
                    return
                }

                setRenderedRows((prevRows) =>
                    prevRows.map((prevRow) => {
                        if (prevRow?.case?.id !== caseId) {
                            return prevRow
                        }

                        const nextRow = dropIds.size === 0 ? prevRow : {
                            ...prevRow,
                            notes: removeDeletedIds(prevRow?.notes, dropIds),
                            task_notes: removeDeletedIds(prevRow?.task_notes, dropIds),
                            tasks: removeDeletedIds(prevRow?.tasks, dropIds),
                        }

                        if (!incomingAicaDepartmentStatus) {
                            return nextRow
                        }

                        return {
                            ...nextRow,
                            aica_department_status: incomingAicaDepartmentStatus,
                            case: {
                                ...nextRow?.case,
                                aica_department_status: incomingAicaDepartmentStatus,
                            },
                        }
                    }),
                )
            }

            socket.on(eventName, handler)
            return { eventName, handler }
        })

        caseSubscriptionsRef.current = subscriptions
        return clearCaseSubscriptions
    }, [patientsList?.data, dispatch])

    useEffect(() => {
        return () => {
            clearAppointmentSubscriptions()
            clearCaseSubscriptions()
        }
    }, [])

    const ITEMS = [
        {
            type: 'day_1_'
        },
        {
            type: 'day_2_'
        }
    ]

    return <>
        <WorkQComponentLoading classes={classes}/>
        {patientsList && <>
            <div className={classes.normal_container}>
                <table className={`${classes.custom_table} ${classes.table_align_top} `}>
                    <thead>
                        <tr>
                            <th colSpan={1}>
                                <HeaderCellGroup item={{groupName: 'Activity'}}/>                               
                            </th> 
                            <th colSpan={2}>
                                <HeaderCellGroup item={{groupName: 'Demographic'}}/>                               
                            </th>    
                            <th colSpan={2}>
                                <HeaderCellGroup item={{groupName: 'Case'}} />
                            </th>                     
                            <th colSpan={5}>
                                <HeaderCellGroup item={{groupName: 'Appointment'}}/>
                            </th> 
                                                
                            <th colSpan={2}>
                                <HeaderCellGroup item={{groupName: 'Follow Up Status'}}/>
                            </th> 
                        </tr>
                        <tr>
                            <th>
                                <HeaderCell item={{name: 'Notes', group: 'patient', label: 'notes', sortable: false}} />
                            </th>
                            {/* demographic  */}
                            <th>
                                <HeaderCell item={{name: 'NG #', group: 'patient', label: 'person_number', sortable: true}} />                                 
                            </th>
                            <th>
                                <HeaderCell item={{name: 'Patient Name', group: 'patient', label: 'name', sortable: true}} />                                 
                            </th> 

                            {/* case  */}
                            <th>
                                <HeaderCell item={{name: 'Type', group: 'case', label: 'case_type_id', sortable: true}} />                                
                            </th>
                            <th className="table-cell-fit">
                                <HeaderCell item={{name: 'Incident Date', group: 'case', label: 'incident_date', sortable: true}} />                                
                            </th> 

                            {/* appointment    */}
                            <th>
                                <HeaderCell item={{name: 'Appointment #', group: 'appointment', label: 'appointment_number', sortable: false}} />                                 
                            </th>
                            <th>
                                <HeaderCell item={{name: 'Class', group: 'appointment', label: 'class', sortable: false}} />                                
                            </th> 
                            <th>
                                <HeaderCell item={{name: 'Date', group: 'appointment', label: 'date', sortable: true}} />                                
                            </th>  
                            <th className="table-cell-fit">
                                <HeaderCell item={{name: 'Begin Time', group: 'appointment', label: 'begin_time', sortable: false}} />                                 
                            </th>
                            <th >
                                <HeaderCell item={{name: 'Description', group: 'appointment', label: 'description', sortable: false}} />                                
                            </th> 
                            
                            {/* <th >
                                <HeaderCell item={{name: 'Outcome', group: 'workq_call_follow_up', label: 'day_1_status', sortable: false}} />                                
                            </th>  */}
                            <th >
                                <HeaderCell item={{name: 'First Call Outcome', group: 'workq_call_follow_up', label: 'day_1_status', sortable: false}} />                                
                            </th>
                            <th >
                                <HeaderCell item={{name: 'Second Call Outcome', group: 'workq_call_follow_up', label: 'day_2_status', sortable: false}} />                                
                            </th>
                        </tr>
                    </thead>
                    <tbody >
                        {renderedRows && renderedRows.map(item => (

                            <tr key={item.appointment?.appointment_number}>

                                {/* Activity */}
                                <td>
                                    <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                        <NoteCell caseData={item} notes={item.notes} workq={retrieveSpecificWorkQCleaned('missed-appts-fu-front-desk')} sub_item={item}/>
                                    </div>
                                    {/* {item.activity && <>
                                        <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                            <TextCellCopy text={item.activity}/>
                                        </div>
                                    </>} */}
                                </td>
                                {/* demographic person #  */}                           
                                <td>
                                    {item.patient && item.patient.person_number && <>                                        
                                        <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                            <TextCellCopy text={item.patient.person_number}/>
                                        </div>
                                    </>}
                                </td>
                                {/* patient name  */}  
                                <td className={`${classes.td_cell_no_padding}`}>
                                    {item.patient && <>
                                        <NameCell slug={workqSlug} item={item} text={item.patient.name} group={{dob: item.patient.dob}} hideDrawer={true} hasphone={true}/>                                
                                    </>}
                                </td>


                                {/* case type  */}
                                <td >
                                    {item.case && <>
                                        <div className={`${classes.cell_row} ${classes.cell_row_flex} ${classes.justify_centered}`}>{getCaseTypeFromId(item.case.case_type_id)}</div>
                                    </>}  
                                </td>
                                {/* incident date  */}
                                <td>
                                    {item.case && <>
                                        <DateWithBox classes={classes} date={item.case.incident_date}/>
                                    </>}
                                </td> 

                                {/* Appointment # */}
                                <td className={`${classes.td_cell_no_padding}`}>
                                    {item.appointment && item.appointment.appointment_number && <>                                        
                                        <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                            <TextCellCopy text={item.appointment.appointment_number}/>
                                        </div>
                                    </>}
                                </td>   
                                {/* class  */}
                                <td className={``}>
                                    {item.appointment?.appointment_classes?.length > 0 && <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                        {[...item.appointment.appointment_classes].sort((a, b) => {
                                            if (String(a).toUpperCase() === 'UC') return -1
                                            if (String(b).toUpperCase() === 'UC') return 1
                                            return 0
                                        }).map((cls) => String(cls).toUpperCase()).join(', ')}
                                    </div>}
                                </td>
                                {/* Date  */}
                                <td className={``}>
                                    {item.appointment && <div className={`${classes.cell_row} ${classes.cell_row_flex} ${classes.cell_row_flex_end}`}>
                                        <DateDisplayWithNmuberOfDays date={item.appointment.date} textRight={true}/>
                                    </div>}
                                </td> 
                                {/* begin time  */}
                                <td className={`table-cell-fit ${dateTimeIsPastPeriod(item?.appointment?.date, item?.appointment?.begin_time, 30) ? classes.yellow_cell : ''} ${dayOneStatusClass(item?.workq_call_follow_up) ? classes.orange_cell : ''}`}>
                                    {item.appointment && <div className={`${classes.cell_row} ${classes.cell_row_flex} ${classes.cell_row_flex_end}`}>
                                        {miscellaneous.convert24to12(item.appointment.begin_time)}
                                    </div>}
                                </td> 
                                {/* description  */}
                                <td className={``}>
                                    {item.appointment && <div className={`${classes.cell_row} ${classes.cell_row_flex}`}>
                                        {item.appointment.description}
                                    </div>}
                                </td>         

                                {item?.workq_call_follow_up && <FollowUpQStatus classes={classes} item={item} items={ITEMS} workQtype={'frontdesk'}/> }
                                {!item?.workq_call_follow_up && <>
                                    <td></td>
                                    <td></td>
                                </>}
                                         
                            
                            </tr>
                        ))}
                    </tbody>
                    
                </table>
            </div>

            
            {patientsList.meta && <div className={classes.pagination_container}>
                {patientsList  && <Pagination data={patientsList} apiType={workqSlug} />}
            </div>}
        </>}
    </>
}